import React, { ReactNode } from "react"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { DELETE_PROJECT_USER_ROLE_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import { DictType } from "@pma/types"
import useTranslator from "@pma/hooks/use-translator"
import { toast } from "@pma/hooks/use-toast"
import { Button } from "@components/common/button"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/common/hover-card"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { getFilteredProjectsByCurrentUser } from "@pma/utils/utils"
import { useAuth } from "@pma/stores/auth-store"
import { Badge } from "@components/common/badge"
import { Separator } from "@components/common/separator"
import { cn } from "@pma/utils/helpers"
import { X } from "lucide-react"

interface Props {
  children: ReactNode
  user: DictType
  refetch: CallableFunction
  projectId?: string
}
const DeleteProjectUserRole = ({ children, user, refetch, projectId }: Props) => {
  const { translate, locale } = useTranslator()
  const queryClient = useQueryClient()

  const { authConfig } = useAuth()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const isHebrew = locale === "he"
  const roleObject = user?.role
  const roleName = roleObject?.name || roleObject?._role?.name
  const isContractor = roleName === "Constructor"

  const orgId = authConfig?.last_organization_id

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 9999,
      }),
  })

  const deleteUserRole = async (roleId: number) => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_PROJECT_USER_ROLE_ENDPOINT + `/${roleId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const deleteUserRoleMutation = useMutation({
    mutationKey: ["delete-role"],
    mutationFn: deleteUserRole,
    onSuccess: () => {
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-role-details"] })
      queryClient.invalidateQueries({ queryKey: ["organization-role-details"] })
      queryClient.invalidateQueries({ queryKey: ["get-project-wise-contractors-list"] })
      toast({
        title: translate("Success"),
        description: translate("Roles user deleted"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const backendMessage = error?.response?.data?.message || error?.message
      const description = isContractor
        ? translate("This user cannot be removed — they are a member of a contractor company")
        : backendMessage || translate("Unable to delete user from role")

      toast({
        title: translate("Error"),
        description,
        variant: "error",
      })
    },
  })

  const getProjectFromId = (projectId: number) => {
    return projects?.items?.find((project: DictType) => project?.id === projectId)
  }

  const activeProjects = user?.projects

  return (
    <HoverCard>
      <HoverCardTrigger className='relative'>
        {children}

        {projectId && isContractor == false && (
          <Button
            onClick={() => deleteUserRoleMutation.mutate(user?.id)}
            size={"icon"}
            className={cn(
              "absolute -top-2 h-3 w-3 rounded-full bg-error-400",
              isHebrew ? "-right-1" : "-left-1",
            )}
          >
            <X />
          </Button>
        )}
      </HoverCardTrigger>
      {!projectId && (
        <HoverCardContent className='md:w-[400px]'>
          <div className='flex space-x-2 pb-1'>
            <Avatar className='relative -ml-2 h-6 w-6'>
              <AvatarImage
                src={user?._user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
              />
            </Avatar>

            <div className='space-y-1'>
              <h4 className='text-sm font-semibold'>{user?._user?.name}</h4>
            </div>
          </div>
          <Separator />
          <p className='p-1 text-slate-600'>
            {translate("Projects with permission")}- <strong>{user?.role?.name}</strong>
          </p>
          <div className='flex w-full flex-wrap gap-2 p-1'>
            {activeProjects?.map((project: DictType, index: number) => (
              <div className='relative' key={index}>
                {isContractor == false && (
                  <Button
                    onClick={() => deleteUserRoleMutation.mutate(project?.role)}
                    size={"icon"}
                    variant={"destructive"}
                    className={cn(
                      "absolute -top-2 h-4 w-4 rounded-full",
                      isHebrew ? "-right-1" : "-left-1",
                    )}
                  >
                    <X />
                  </Button>
                )}

                <Badge variant={"default"} key={project?.id}>
                  {getProjectFromId(project?.id)?.title}
                </Badge>
              </div>
            ))}
          </div>
          <Separator />
        </HoverCardContent>
      )}
    </HoverCard>
  )
}

export default DeleteProjectUserRole
